/**
 * useLeaderboard - Custom hook for fetching and managing leaderboard data
 * Used by both Rankings (private) and PublicLeaderboard (public) pages
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import rankingsService from '../services/rankingsService'
import availableStrategiesService from '../services/availableStrategiesService'

import { createLogger } from '../utils/logger'

const logger = createLogger('useLeaderboard')

const PAGE_SIZE = 15

// Strategy icon mapping
const STRATEGY_ICONS = {
  SAT_MATH: '🧮',
  SAT_MATH_MINI: '⚡',
  SAT_READING_WRITING: '📖',
  SAT_READING_WRITING_MINI: '✏️'
}

export const getStrategyIcon = (strategyType) => {
  return STRATEGY_ICONS[strategyType] || '📝'
}

/**
 * Hook for managing leaderboard state and data fetching
 */
export function useLeaderboard({
  initialStrategy = null,
  initialPeriod = 'OVERALL_BEST',
  highlightUser = null,
  userId = null
} = {}) {
  // State
  const [selectedStrategy, setSelectedStrategy] = useState(initialStrategy)
  const [selectedPeriod, setSelectedPeriod] = useState(initialPeriod)
  const [leaderboard, setLeaderboard] = useState({ entries: [], pagination: {} })
  const [loading, setLoading] = useState(true)
  const [leaderboardLoading, setLeaderboardLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [strategyTypes, setStrategyTypes] = useState([])
  const [loadError, setLoadError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [selectedLevel, setSelectedLevel] = useState(null)
  const [highlightedUserInfo, setHighlightedUserInfo] = useState(null)
  const [userPosition, setUserPosition] = useState(null)

  const initialStrategySet = useRef(false)

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Load available strategies on mount
  useEffect(() => {
    const loadStrategies = async () => {
      try {
        setLoadError(null)
        const strategies = await availableStrategiesService.getAvailableStrategies()
        const formattedStrategies = strategies.map((s) => ({
          type: s.strategyType || s.type,
          name:
            s.name || availableStrategiesService.getStrategyDisplayName(s.strategyType || s.type),
          icon: getStrategyIcon(s.strategyType || s.type)
        }))
        setStrategyTypes(formattedStrategies)

        // Set initial strategy if not already set
        if (formattedStrategies.length > 0 && !initialStrategySet.current) {
          const validInitial =
            initialStrategy && formattedStrategies.some((s) => s.type === initialStrategy)
          setSelectedStrategy(validInitial ? initialStrategy : formattedStrategies[0].type)
          initialStrategySet.current = true
        }
      } catch (error) {
        logger.error('Error loading strategies:', error)
        setLoadError('Failed to load rankings data')
      } finally {
        setLoading(false)
      }
    }
    loadStrategies()
  }, [initialStrategy])

  // Load leaderboard when filters change
  const loadLeaderboard = useCallback(async () => {
    if (!selectedStrategy) return
    setLeaderboardLoading(true)
    try {
      const levelFilters = selectedLevel ? { minLevel: selectedLevel, maxLevel: selectedLevel } : {}

      const data = await rankingsService.getLeaderboard(selectedStrategy, {
        period: selectedPeriod,
        limit: PAGE_SIZE,
        offset: currentPage * PAGE_SIZE,
        ...levelFilters,
        search: debouncedSearchTerm || undefined
      })
      setLeaderboard(data)

      // Fetch user's position if logged in
      if (userId) {
        const position = await rankingsService.getMyPosition(
          selectedPeriod,
          selectedStrategy,
          levelFilters
        )
        if (position) {
          setUserPosition({
            rank: position.rank,
            total: position.total,
            level: selectedLevel,
            entry: position.entry
          })
        } else if (selectedLevel) {
          setUserPosition({ notInLevel: true, level: selectedLevel })
        } else {
          setUserPosition(null)
        }
      } else {
        setUserPosition(null)
      }
    } catch (error) {
      logger.error('Error loading leaderboard:', error)
    } finally {
      setLeaderboardLoading(false)
    }
  }, [selectedStrategy, selectedPeriod, currentPage, selectedLevel, debouncedSearchTerm, userId])

  useEffect(() => {
    loadLeaderboard()
  }, [loadLeaderboard])

  // Load highlighted user data when URL contains a highlight param
  useEffect(() => {
    const loadHighlightedUser = async () => {
      if (!highlightUser || !selectedStrategy) return

      try {
        const data = await rankingsService.getUserPositionByName(
          highlightUser,
          selectedStrategy,
          selectedPeriod
        )

        if (data) {
          setHighlightedUserInfo({
            publicName: data.user.publicName,
            rank: data.user.rank,
            total: data.user.total
          })
          // Override leaderboard with centered entries
          setLeaderboard({
            entries: data.entries,
            pagination: data.pagination
          })
        } else {
          setHighlightedUserInfo(null)
        }
      } catch (error) {
        logger.error('Error loading highlighted user:', error)
        setHighlightedUserInfo(null)
      }
    }

    loadHighlightedUser()
  }, [highlightUser, selectedStrategy, selectedPeriod])

  // Handlers
  const handleStrategyChange = useCallback((strategy) => {
    setSelectedStrategy(strategy)
    setCurrentPage(0)
  }, [])

  const handlePeriodChange = useCallback((period) => {
    setSelectedPeriod(period)
    setCurrentPage(0)
  }, [])

  const handleLevelChange = useCallback((level) => {
    setSelectedLevel(level)
    setCurrentPage(0)
  }, [])

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page)
  }, [])

  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term)
  }, [])

  const clearSearch = useCallback(() => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setCurrentPage(0)
  }, [])

  const clearFilters = useCallback(() => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setSelectedLevel(null)
    setCurrentPage(0)
  }, [])

  return {
    // State
    selectedStrategy,
    selectedPeriod,
    selectedLevel,
    leaderboard,
    loading,
    leaderboardLoading,
    currentPage,
    strategyTypes,
    loadError,
    searchTerm,
    debouncedSearchTerm,
    highlightedUserInfo,
    userPosition,
    pageSize: PAGE_SIZE,
    hasActiveFilters: searchTerm || selectedLevel,

    // Handlers
    handleStrategyChange,
    handlePeriodChange,
    handleLevelChange,
    handlePageChange,
    handleSearchChange,
    clearSearch,
    clearFilters,
    refresh: loadLeaderboard
  }
}

export default useLeaderboard
